import { AdvancedEditCut } from '@app/routes/content/routes/recordings/domain';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { FlatWord } from '../utils/build-flat-words';
import { findGapWordIndices } from '../utils/split-cut';

// ── Internal state shape ──────────────────────────────────────────────────────

type SelectionState =
  | { kind: 'idle' }
  | { kind: 'words'; anchorIdx: number; focusIdx: number; isSelecting: boolean }
  | { kind: 'cut-gap'; gapIndices: number[]; clickedIdx: number };

// ── Public return type ────────────────────────────────────────────────────────

export type UseWordSelectionReturn = {
  /** [start, end] range of selected non-cut words, or null when nothing selected. */
  selectedRange: { start: number; end: number } | null;
  /** Flat words within selectedRange (empty when gap is active). */
  selectedWords: FlatWord[];
  /** Flat indices of all words in the clicked gap, or null. */
  cutGapIndices: number[] | null;
  /** Flat words for all gap indices (empty when normal selection is active). */
  cutGapWords: FlatWord[];
  /**
   * Union of normal-selection indices + gap indices.
   * Consumed by segment-group to determine which words get the highlight style.
   */
  highlightedIndices: Set<number>;
  handleWordMouseDown: (flatIdx: number) => void;
  handleWordMouseEnter: (flatIdx: number) => void;
  handleMouseUp: () => void;
  clearSelection: () => void;
};

const IDLE: SelectionState = { kind: 'idle' };

/**
 * Drag-selection + cut-gap state machine for the EditTranscript word list.
 *
 * Rules:
 *   - Clicking a non-cut word starts a normal drag selection.
 *   - Clicking a cut word jumps to cut-gap mode — the entire deleted gap is
 *     highlighted and no drag is possible.
 *   - Dragging never crosses a cut boundary.
 *   - A global mouseup listener ends any in-progress drag.
 */
export const useWordSelection = (
  flatWords: FlatWord[],
  cuts: AdvancedEditCut[],
): UseWordSelectionReturn => {
  const [state, setState] = useState<SelectionState>(IDLE);

  // Keep a ref so the global mouseup handler always sees the latest state.
  const stateRef = useRef<SelectionState>(IDLE);
  stateRef.current = state;

  // ── Event handlers ────────────────────────────────────────────────────────

  const handleWordMouseDown = useCallback(
    (flatIdx: number) => {
      const fw = flatWords[flatIdx];
      if (!fw) return;

      if (fw.isCut) {
        // Clicking a cut word → select the full deleted gap
        const gapIndices = findGapWordIndices(flatWords, flatIdx, cuts);
        setState({ kind: 'cut-gap', gapIndices, clickedIdx: flatIdx });
      } else {
        // Clicking a non-cut word → start normal selection
        setState({
          kind: 'words',
          anchorIdx: flatIdx,
          focusIdx: flatIdx,
          isSelecting: true,
        });
      }
    },
    [flatWords, cuts],
  );

  const handleWordMouseEnter = useCallback(
    (flatIdx: number) => {
      if (stateRef.current.kind !== 'words') return;
      if (!stateRef.current.isSelecting) return;

      const fw = flatWords[flatIdx];
      // Never extend selection into cut words
      if (!fw || fw.isCut) return;

      setState(prev => {
        if (prev.kind !== 'words') return prev;
        return { ...prev, focusIdx: flatIdx };
      });
    },
    [flatWords],
  );

  const handleMouseUp = useCallback(() => {
    setState(prev => {
      if (prev.kind !== 'words') return prev;
      return { ...prev, isSelecting: false };
    });
  }, []);

  const clearSelection = useCallback(() => {
    setState(IDLE);
  }, []);

  // Global mouseup: end drag if released outside the component
  useEffect(() => {
    const onGlobalMouseUp = () => {
      if (stateRef.current.kind === 'words' && stateRef.current.isSelecting) {
        setState(prev => {
          if (prev.kind !== 'words') return prev;
          return { ...prev, isSelecting: false };
        });
      }
    };
    document.addEventListener('mouseup', onGlobalMouseUp);
    return () => document.removeEventListener('mouseup', onGlobalMouseUp);
  }, []);

  // ── Derived values ────────────────────────────────────────────────────────

  const selectedRange = useMemo((): { start: number; end: number } | null => {
    if (state.kind !== 'words') return null;
    return {
      start: Math.min(state.anchorIdx, state.focusIdx),
      end: Math.max(state.anchorIdx, state.focusIdx),
    };
  }, [state]);

  const selectedWords = useMemo((): FlatWord[] => {
    if (selectedRange === null) return [];
    return flatWords.slice(selectedRange.start, selectedRange.end + 1);
  }, [flatWords, selectedRange]);

  const cutGapIndices = useMemo((): number[] | null => {
    if (state.kind !== 'cut-gap') return null;
    return state.gapIndices;
  }, [state]);

  const cutGapWords = useMemo((): FlatWord[] => {
    if (cutGapIndices === null) return [];
    return cutGapIndices.map(idx => flatWords[idx]).filter(Boolean);
  }, [flatWords, cutGapIndices]);

  const highlightedIndices = useMemo((): Set<number> => {
    const set = new Set<number>();
    if (selectedRange !== null) {
      for (let i = selectedRange.start; i <= selectedRange.end; i++) {
        set.add(i);
      }
    }
    if (cutGapIndices !== null) {
      for (const idx of cutGapIndices) {
        set.add(idx);
      }
    }
    return set;
  }, [selectedRange, cutGapIndices]);

  return {
    selectedRange,
    selectedWords,
    cutGapIndices,
    cutGapWords,
    highlightedIndices,
    handleWordMouseDown,
    handleWordMouseEnter,
    handleMouseUp,
    clearSelection,
  };
};
